import './list_table_component.scss'
import { Controller } from 'stimulus'

interface LoadMoreDataAttr {
  blockId: string
  nextPageUrl: string
  totalPages: string
}
export default class extends Controller {
  static targets = ['table', 'headerCell', 'bodyCell', 'loadMore', 'spinner']
  static classes = ['split', 'start', 'spacer', 'end', 'startFade', 'endFade']
  page = 2

  connect = (): void => {
    this.addStickyClasses()
  }

  loadMore = (): void => {
    const { blockId, nextPageUrl, totalPages }: LoadMoreDataAttr = this.element.dataset
    const pageParam: string = `${blockId}_page=`
    let url: string = `${nextPageUrl}&block_id=${blockId}`
    url = url.replace(
      `${pageParam}2`,
      `${pageParam}${this.page}`
    )

    this.toggleSpinner()

    fetch(url, {
      headers: {
        Accept: 'application/json'
      }
    })
      .then(async (response) => await response.json())
      .then((data) => {
        this.tableTarget.insertAdjacentHTML('beforeend', data.join(''))
        this.addStickyClasses()
        this.toggleSpinner()
        this.hideLoadMoreButton(parseInt(totalPages))
        this.page += 1
      })
      .catch((er) => {
        throw new Error(er)
      })
  }

  reflow = (): void => {
    this.padCells()
    this.addFade()
  }

  hideLoadMoreButton = (totalPages: number): void => {
    if (totalPages === this.page) {
      this.loadMoreTarget.style.display = 'none'
    }
  }

  increase = (): void => {
    this.element.classList.remove(this.splitClass)
  }

  decrease = (): void => {
    this.element.classList.add(this.splitClass)
  }

  cells = (position: number): object[] => {
    return [...this.headerCellTargets, ...this.bodyCellTargets].filter(
      (cell) => cell.getAttribute('data-position') === String(position)
    )
  }

  padCells = (): void => {
    if (!this.hasStickyEnd) return
    this.calculateOffsets()
    this.stickyEndCells.forEach((cell) => {
      cell.style = `right: ${this.offset(cell)};`
    })

    this.spacerCells.forEach((cell) => {
      cell.style = `right: ${this.offset(cell)};`
    })
  }

  calculateOffsets = (): void => {
    let offset = 0
    this.headerCellTargets.reverse().forEach((th) => {
      th.setAttribute('data-offset', `${offset}px`)
      offset += parseFloat(th.offsetWidth)
    })
  }

  offset = (cell: object): string => {
    const position = cell.getAttribute('data-position')
    return this.headerCellTargets[position].getAttribute('data-offset')
  }

  addFade = (event): void => {
    if (this.scrolledFromStart) {
      this.element.classList.add(this.startFadeClass)
    } else {
      this.element.classList.remove(this.startFadeClass)
    }
    if (this.scrolledToEnd) {
      this.element.classList.remove(this.endFadeClass)
    } else {
      this.element.classList.add(this.endFadeClass)
    }
  }

  addStickyClasses = (): void => {
    if (this.hasStickyStart) {
      this.stickyStartCells.forEach((cell) => {
        cell.classList.add(this.startClass)
      })
    }

    if (this.hasStickyEnd) {
      this.stickyEndCells.forEach((cell) => {
        cell.classList.add(this.endClass)
      })

      this.spacerCells.forEach((cell) => {
        cell.classList.add(this.spacerClass)
      })
    }

    this.reflow()
  }

  toggleSpinner = (): void => {
    this.spinnerTarget.classList.toggle('hidden')

    if (this.loadMoreTarget.style.display !== 'none') {
      this.loadMoreTarget.style.display = 'none'
    } else {
      this.loadMoreTarget.style.display = 'inline-flex'
    }
  }

  get split (): boolean {
    return this.element.classList.contains(this.splitClass)
  }

  // Every table has a sticky first column
  get hasStickyStart (): boolean {
    return true
  }

  get hasStickyEnd (): boolean {
    return this.stickyEndCount > 0
  }

  get stickyEndPosition (): number {
    return this.columnCount - this.stickyEndCount
  }

  // Count empty header cells from end to decide how many (counting from the end) need to be made sticky
  get stickyEndCount (): number {
    let stickyEndCount = 0
    for (const cell of this.headerCellTargets.reverse()) {
      if (cell.children[0].innerHTML.length === 0) {
        stickyEndCount += 1
      } else {
        break
      }
    }
    return stickyEndCount
  }

  get stickyStartCells (): object[] {
    return this.cells(0)
  }

  get stickyEndCells (): object[] {
    return this.cells(this.stickyEndPosition)
  }

  get spacerCells (): object[] {
    const spacerCells = []
    const lastSpacerColumnPosition = this.columnCount - 1
    for (let i = lastSpacerColumnPosition; i > this.stickyEndPosition; i--) {
      spacerCells.push(...this.cells(i))
    }
    return spacerCells
  }

  get columnCount (): number {
    return this.headerCellTargets.length
  }

  get scrolledFromStart (): boolean {
    return this.element.scrollLeft > 0
  }

  get scrolledToEnd (): boolean {
    return (
      this.element.scrollLeft + window.innerWidth >=
      this.tableTarget.offsetWidth
    )
  }
}
